import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { fieldClass, labelClass, primaryBtnClass } from '../styles';
import type {
  AiGeneratorConfig,
  AiModelConfig,
  AiModelOption,
  AiPromptTemplate,
  GeneratorKey,
} from '../../aitest/types';
import ModelPicker, { configForModel, modelConfigPayload } from '../../aitest/ModelPicker';
import {
  listAdminAiModels,
  listAdminGeneratorConfigs,
  listAdminPromptTemplates,
  patchAdminGeneratorConfig,
  saveAdminGeneratorConfig,
} from './api';

interface GeneratorConfigPanelProps {
  generatorKey: GeneratorKey;
  title: string;
  enabled: boolean;
  defaultVariableKeys: string[];
  extraSteps?: Array<{ key: string; label: string }>;
  includeDefaultStep?: boolean;
  hidePrompt?: boolean;
  hideVariables?: boolean;
}

const GeneratorConfigPanel: React.FC<GeneratorConfigPanelProps> = ({
  generatorKey,
  title,
  enabled,
  defaultVariableKeys,
  extraSteps = [],
  includeDefaultStep = true,
  hidePrompt = false,
  hideVariables = false,
}) => {
  const [configs, setConfigs] = useState<AiGeneratorConfig[]>([]);
  const [prompts, setPrompts] = useState<AiPromptTemplate[]>([]);
  const [models, setModels] = useState<AiModelOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const derivedExtraSteps = useMemo(() => {
    if (extraSteps.length) return extraSteps;
    const keys = Array.from(
      new Set(prompts.map((p) => p.step_key || '').filter(Boolean))
    ).sort();
    return keys.map((key) => ({
      key,
      label: key.replace(/_/g, ' '),
    }));
  }, [extraSteps, prompts]);

  const steps = includeDefaultStep
    ? [{ key: '', label: title }, ...derivedExtraSteps]
    : derivedExtraSteps;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [cfg, promptRows, modelRows] = await Promise.all([
          listAdminGeneratorConfigs(generatorKey),
          listAdminPromptTemplates(generatorKey),
          listAdminAiModels(generatorKey),
        ]);
        if (cancelled) return;
        setConfigs(cfg);
        setPrompts(promptRows);
        setModels(modelRows.filter((m) => m.is_active));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load config');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [generatorKey]);

  if (!enabled) {
    return (
      <p className="text-sm text-[#8A8A8A]">
        {title} configuration will be enabled in a later slice. The production path is unchanged.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#8A8A8A]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {message && <p className="text-sm text-emerald-700">{message}</p>}
      {steps.map((step) => (
        <StepForm
          key={step.key || 'default'}
          generatorKey={generatorKey}
          stepKey={step.key}
          label={step.label}
          config={configs.find((c) => c.step_key === step.key)}
          prompts={prompts.filter((p) => (p.step_key || '') === step.key)}
          models={models}
          defaultVariableKeys={defaultVariableKeys}
          hidePrompt={hidePrompt}
          hideVariables={hideVariables}
          saving={saving}
          onSave={async (payload) => {
            setSaving(true);
            setError(null);
            setMessage(null);
            try {
              const saved = payload.id
                ? await patchAdminGeneratorConfig(payload.id, payload)
                : await saveAdminGeneratorConfig(payload);
              setConfigs((prev) => {
                const without = prev.filter((c) => !(c.generator_key === generatorKey && c.step_key === step.key));
                return [...without, saved];
              });
              setMessage('Saved. Client runs now use this configuration.');
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Could not save');
            } finally {
              setSaving(false);
            }
          }}
        />
      ))}
    </div>
  );
};

interface StepFormProps {
  generatorKey: GeneratorKey;
  stepKey: string;
  label: string;
  config?: AiGeneratorConfig;
  prompts: AiPromptTemplate[];
  models: AiModelOption[];
  defaultVariableKeys: string[];
  hidePrompt?: boolean;
  hideVariables?: boolean;
  saving: boolean;
  onSave: (payload: Partial<AiGeneratorConfig> & { generator_key: GeneratorKey }) => Promise<void>;
}

const StepForm: React.FC<StepFormProps> = ({
  generatorKey,
  stepKey,
  label,
  config,
  prompts,
  models,
  defaultVariableKeys,
  hidePrompt,
  hideVariables,
  saving,
  onSave,
}) => {
  const [promptId, setPromptId] = useState<number | ''>(config?.prompt || '');
  const [modelId, setModelId] = useState(String(config?.model_config?.model_id || ''));
  const [modelConfig, setModelConfig] = useState<AiModelConfig>(() => {
    const raw = config?.model_config && typeof config.model_config === 'object' ? config.model_config : {};
    const { model_id: _ignored, ...rest } = raw as Record<string, unknown>;
    return rest as AiModelConfig;
  });
  const [variableKeys, setVariableKeys] = useState<string[]>(
    config?.variable_keys?.length ? config.variable_keys : defaultVariableKeys
  );

  useEffect(() => {
    setPromptId(config?.prompt || '');
    const raw = config?.model_config && typeof config.model_config === 'object' ? config.model_config : {};
    const nextId = String((raw as Record<string, unknown>).model_id || '');
    setModelId(nextId);
    const selected = models.find((m) => m.model_id === nextId);
    const { model_id: _ignored, ...rest } = raw as Record<string, unknown>;
    setModelConfig(configForModel(selected, rest as AiModelConfig, generatorKey));
    setVariableKeys(config?.variable_keys?.length ? config.variable_keys : defaultVariableKeys);
  }, [config, defaultVariableKeys, generatorKey, models]);

  const toggleVar = (key: string) => {
    setVariableKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  return (
    <div className="rounded-lg border border-[#E8E8E8] p-4 space-y-3">
      <h3 className="text-sm font-semibold text-[#0C0B0B]">{label}</h3>
      {!hidePrompt && (
      <div>
        <label className={labelClass}>Prompt</label>
        <select
          value={promptId}
          onChange={(e) => setPromptId(e.target.value ? Number(e.target.value) : '')}
          className={fieldClass}
        >
          <option value="">None (keep current production prompt)</option>
          {prompts.map((p) => (
            <option key={p.id || p.blob_filename} value={p.id || ''}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      )}
      <ModelPicker
        models={models}
        modelId={modelId}
        onModelId={setModelId}
        config={modelConfig}
        onConfig={setModelConfig}
        generatorKey={generatorKey}
        allowEmpty
        emptyLabel="Default pipeline model"
      />
      {!hideVariables && (
      <div>
        <label className={labelClass}>Variables sent into the prompt</label>
        <div className="flex flex-wrap gap-3">
          {defaultVariableKeys.map((key) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={variableKeys.includes(key)}
                onChange={() => toggleVar(key)}
              />
              {key}
            </label>
          ))}
        </div>
      </div>
      )}
      <button
        type="button"
        disabled={saving}
        onClick={() =>
          void onSave({
            id: config?.id,
            generator_key: generatorKey,
            step_key: stepKey,
            prompt: hidePrompt ? null : promptId || null,
            model_config: modelId ? modelConfigPayload(modelId, modelConfig) : {},
            variable_keys: hideVariables ? [] : variableKeys,
            is_active: true,
          })
        }
        className={primaryBtnClass}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
};

export default GeneratorConfigPanel;
